if (!customElements.get('pv-donation-summary')) {
  customElements.define('pv-donation-summary', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.amount = this.querySelector('[data-pv-donation-amount]');
      this.status = this.querySelector('[data-pv-donation-status]');
      this.live = this.querySelector('[data-pv-donation-live]');
      this.liveLabel = this.querySelector('[data-pv-donation-label]');
      if (!this.amount || !this.status || !this.live || !this.liveLabel) return;
      this.initialized = true;
      this.inView = false;
      this.failed = false;
      this.value = null;
      const locale = document.documentElement.lang || 'de-DE';
      this.moneyFormat = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' });
      this.dateFormat = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      this.acceptValue(this.amount);
      this.updatePresentation();
      this.onActivity = () => this.syncActivity();
      document.addEventListener('visibilitychange', this.onActivity);
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries.some(entry => entry.isIntersecting);
        this.syncActivity();
      });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.initialized = false;
      window.clearTimeout(this.timer);
      window.clearTimeout(this.freshnessTimer);
      this.controller?.abort();
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.onActivity);
    }

    isActive() {
      return this.isConnected && this.inView && !document.hidden && this.getClientRects().length > 0;
    }

    syncActivity() {
      window.clearTimeout(this.timer);
      this.updatePresentation();
      if (!this.isActive()) {
        this.controller?.abort();
        return;
      }
      if (!this.controller) this.refresh();
    }

    acceptValue(source) {
      const rawCents = source?.dataset.totalCents || '';
      const cents = Number(rawCents);
      const updatedAt = source?.dataset.updatedAt || '';
      const timestamp = Date.parse(updatedAt);
      if (source?.dataset.state !== 'ready' || source.dataset.currency !== 'EUR' || !/^\d+$/.test(rawCents) || !Number.isSafeInteger(cents) || cents < 0 || !Number.isFinite(timestamp) || timestamp <= 0 || timestamp > Date.now() + 300000) return false;
      if (this.value && timestamp < this.value.timestamp) return false;
      this.value = { cents, timestamp, updatedAt, automatic: source.dataset.automatic === 'true' };
      this.failed = false;
      this.amount.dataset.state = 'ready';
      this.amount.dataset.totalCents = rawCents;
      this.amount.dataset.currency = 'EUR';
      this.amount.dataset.automatic = String(this.value.automatic);
      this.amount.dataset.updatedAt = updatedAt;
      return true;
    }

    updatePresentation() {
      window.clearTimeout(this.freshnessTimer);
      const age = this.value ? Date.now() - this.value.timestamp : Infinity;
      const active = Boolean(this.value && this.value.automatic && age >= 0 && age <= 900000 && !this.failed);
      if (active && this.isConnected) this.freshnessTimer = window.setTimeout(() => this.updatePresentation(), 900001 - age);
      this.live.dataset.active = String(active);
      const label = active ? this.dataset.liveLabel : this.dataset.collectedLabel;
      if (this.liveLabel.textContent !== label) this.liveLabel.textContent = label;
      if (!this.value) {
        this.amount.textContent = '—';
        this.amount.dataset.state = 'unavailable';
        this.status.textContent = this.dataset.unavailableText;
        return;
      }
      const formatted = this.moneyFormat.format(this.value.cents / 100);
      if (this.amount.textContent !== formatted) this.amount.textContent = formatted;
      this.amount.style.setProperty('--pv-donation-digits', String(formatted.length));
      const stamp = `${this.dataset.updatedLabel} ${this.dateFormat.format(this.value.timestamp)}`;
      const stale = this.failed || (this.value.automatic && age > 900000);
      const status = stale ? `${this.dataset.staleText} · ${stamp}` : stamp;
      if (this.status.textContent !== status) this.status.textContent = status;
    }

    async refresh() {
      if (!this.isActive() || this.controller || !this.dataset.sectionId) return;
      this.updatePresentation();
      const controller = new AbortController();
      this.controller = controller;
      let timedOut = false;
      const timeout = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, 12000);
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('_pv_donation', String(Date.now()));
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Donation section unavailable');
        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const counter = Array.from(fragment.querySelectorAll('pv-donation-summary')).find(element => element.dataset.sectionId === this.dataset.sectionId && element.dataset.instanceId === this.dataset.instanceId);
        if (!this.acceptValue(counter?.querySelector('[data-pv-donation-amount]'))) this.failed = true;
      } catch {
        if (timedOut || !controller.signal.aborted) this.failed = true;
      } finally {
        window.clearTimeout(timeout);
        if (this.controller === controller) this.controller = null;
        this.updatePresentation();
        if (this.isActive()) this.timer = window.setTimeout(() => this.refresh(), 30000);
      }
    }
  });
}
